import { Button, Flex, ModuleLayout, Space, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'
import { useIsDemoInvoiceEditorEnabled } from '@features/demoInvoice/hooks/useIsDemoInvoiceEditorEnabled'
import { SkipQuickStartConfirmationModal } from '@features/quickStart/elements/SkipQuickStartConfirmationModal'
import { QuickStartStep } from '@features/quickStart/enums'
import { useQuickStartProgress } from '@features/quickStart/hooks/useQuickStartProgress'
import { useTrackQuickStart } from '@features/quickStart/hooks/useTrackQuickStart'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EmberRoute } from '../../enums/emberRoute'
import { ModalId } from '../../enums/modalId'
import { useEmberRouteUrl } from '../../hooks/routing/useEmberRouteUrl'
import { useIsCpaOrganization } from '../../hooks/useIsCpaOrganization'
import { getOrganizationViewUrl } from '../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../utils/routing/reactRoute'
import { QuickStartAccordion } from './elements/QuickStartAccordion'
import { QuickStartHero } from './elements/QuickStartHero'
import * as Styled from './styles'
import { getQuickStartItems } from './utils/getQuickStartItems'

export const QuickStart = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { url: settingsOrganizationUrl } = useEmberRouteUrl(EmberRoute.SettingsOrganization)
  const { completionByStep, isCompleted, isEligible, isLoading, isSkipped } = useQuickStartProgress()
  const { isCpaOrganization } = useIsCpaOrganization()
  const isDemoInvoiceEditorEnabled = useIsDemoInvoiceEditorEnabled()
  const { isTransactionsOnboardingEnabled } = useBankTransactionsAccess()
  const { open: openSkipQuickStartConfirmationModal } = useModal(ModalId.SkipQuickStartConfirmationModal)
  const { trackQuickStart } = useTrackQuickStart()

  const quickStartItems = useMemo(
    () =>
      getQuickStartItems({
        isBankDemoEnabled: isTransactionsOnboardingEnabled,
        isCpa: isCpaOrganization,
        isDemoInvoiceEditorEnabled,
        organizationUrl: organization?.url,
        settingsOrganizationUrl,
        t,
      }).filter((quickStartItem) => quickStartItem.id in completionByStep),
    [
      isCpaOrganization,
      isDemoInvoiceEditorEnabled,
      isTransactionsOnboardingEnabled,
      organization?.url,
      settingsOrganizationUrl,
      t,
      completionByStep,
    ],
  )

  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null)
  const hasSetInitialOpenAccordion = useRef(false)

  useEffect(() => {
    if (isLoading) {
      return
    }

    setOpenAccordionId((currentOpenAccordionId) => {
      const isInitialPick = !hasSetInitialOpenAccordion.current
      hasSetInitialOpenAccordion.current = true

      if (!isInitialPick && !(currentOpenAccordionId && completionByStep[currentOpenAccordionId as QuickStartStep])) {
        return currentOpenAccordionId
      }

      return quickStartItems.find((accordionItem) => !completionByStep[accordionItem.id])?.id ?? null
    })
  }, [isLoading, quickStartItems, completionByStep])

  const handleToggle = useCallback((accordionId: string) => {
    setOpenAccordionId((currentOpenAccordionId) => (currentOpenAccordionId === accordionId ? null : accordionId))
  }, [])

  const handleSkipButtonClick = useCallback(() => {
    trackQuickStart('XXX - AG - Quick Start - Dismiss Quick Start clicked')
    openSkipQuickStartConfirmationModal()
  }, [openSkipQuickStartConfirmationModal, trackQuickStart])

  if (isSkipped || (!isLoading && !isEligible)) {
    return (
      <Navigate
        to={getOrganizationViewUrl(organization?.url, reactRoute.organization.dashboard.getMainRoute())}
        replace
      />
    )
  }

  return (
    <ModuleLayout>
      <Styled.QuickStartContent>
        <QuickStartHero />
        <Space size="s24" />
        {quickStartItems.map((accordionItem) => (
          <QuickStartAccordion
            key={accordionItem.id}
            {...accordionItem}
            isCompleted={Boolean(completionByStep[accordionItem.id])}
            isOpen={!completionByStep[accordionItem.id] && openAccordionId === accordionItem.id}
            onToggle={handleToggle}
          />
        ))}
        <Flex justifyContent="flex-end">
          <Button data-testid="quick-start-skip" onClick={handleSkipButtonClick} type="button" variant="text">
            {t(isCompleted ? 'quick_start.dismiss' : 'quick_start.skip')}
          </Button>
        </Flex>
      </Styled.QuickStartContent>
      <SkipQuickStartConfirmationModal />
    </ModuleLayout>
  )
}
